import React, {
  FunctionComponent as FC,
  useRef,
  useState,
} from 'react';
import * as d3 from 'd3';
import _ from 'lodash';
import './index.scss';

import { MainViewTypes } from '../../../../../../types/data';
import { State } from '../../../../../../state';
import { useSelector } from 'react-redux';

import PredictionGlyph from '../PredictionGlyph';

interface RCIGlyphProps {
  id: number;
  grantGroupName: string;
  // grant_field_name: string;
  glyphDataHistorical: any;
  glyphDataPredicted: any;
  attributeScale: any;
  attributeNameDict: any;
  glyphParameterDict: any;
  glyphStyle: any;
  centerGrantNode_MaxMin: number[];
}

const RCIGlyph: FC<RCIGlyphProps> = (props) => {

  // reference: radar chart (use lineRadial)
  // https://gist.github.com/nbremer/21746a9668ffdf6d8242

  const { id, grantGroupName,
          glyphDataHistorical, glyphDataPredicted,
          // glyphData,
          attributeScale, attributeNameDict, glyphParameterDict, glyphStyle, centerGrantNode_MaxMin } = props

  // console.log('RCIGlyph')
  // console.log('glyphData: ', glyphData)
  // console.log('grantGroupName: ', grantGroupName)
  // console.log('attributeNameDict: ', attributeNameDict)
  // console.log('attributeScale: ', attributeScale)


  // ui reducer
  const highlightedImpactFieldNode: MainViewTypes.HighlightedImpactFieldNode = useSelector((state: State) => state.dataReducer?.highlightedImpactFieldNode) as MainViewTypes.HighlightedImpactFieldNode

  const [isVisibleDirectImpactAxis, setIsVisibleDirectImpactAxis] = useState(false)
  const [isVisibleIndirectImpactAxis, setIsVisibleIndirectImpactAxis] = useState(false)
  const [textLabelVisibility, setTextLabelVisibility] = useState('hidden')

  // draw glyph
  const drawImpactDotCurve = (attrNameList: any, scale: any, glyphData: any, impactType: string) => {
    // 1. set up the line generator and angle step
    let angleStep = 2 * Math.PI / attrNameList.length // Calculate the angle for each attribute
    const lineGenerator = d3.lineRadial()
      .angle((d: any) => d.angle) // replace with your angle scale function
      .radius((d: any) => scale(d.value)) // replace with your radius scale function
      .curve(d3.curveCardinalClosed.tension(-0.2)); // or another curve generator

    // 2. draw axis
    // console.log('attrNameList: ', attrNameList)
    const axisElementList: JSX.Element[] = attrNameList.map((attr: any, i: number) => {
      let parts = attr.split("_")

      let entity = parts[parts.length - 1]
      if (parts[2] !== 'citing') {
        entity = parts.slice(2).join('_')
      }


      const displayAttrName = impactType === 'directImpact' ? isVisibleDirectImpactAxis : isVisibleIndirectImpactAxis
      return (
        <g key={`RCI-glyph-axis-g-${i}-${impactType}`} id={`RCI-glyph-axis-g-${i}-${impactType}`} style={{ display: displayAttrName ? 'block' : 'none' }}
        >
          {/* <circle cx={scale(1) * Math.cos(i * angleStep - Math.PI/2)}
                  cy={scale(1) * Math.sin(i * angleStep - Math.PI/2)}
                  r={2} fill='#333333'
          /> */}
          <text
            x={scale(1) * Math.cos(i * angleStep - Math.PI/2)}
            y={scale(1) * Math.sin(i * angleStep - Math.PI/2)}
            fontSize={8}
            fill='#747474'
            transform={ (parts[2] !== 'citing') ? `translate(-4, 0)` : `translate(-12, 0)`}
          >
            {entity}
          </text>
          <line id='RCI-glyph-axis'
            x1={0} y1={0}
            x2={scale(1) * Math.cos(i * angleStep - Math.PI/2)}
            y2={scale(1) * Math.sin(i * angleStep - Math.PI/2)}
            stroke="#CDCDCD" strokeDasharray={"3 4"} strokeWidth={1}
          />
        </g>
      )
    })

    // 3. draw dot
    // calculate the position for each dot
    let dots: { x: number; y: number, attrName: string, value: number, diff_value_to_baseline: number, angle: number }[] = [];
    attrNameList.forEach((attr: any, i: number) => {
      let angle = i * angleStep;
      let value = glyphData[attr]
      dots.push({
          x: scale(value) * Math.cos(angle - Math.PI/2),
          y: scale(value) * Math.sin(angle - Math.PI/2),
          attrName: attr,
          value: value,
          diff_value_to_baseline: value - 1,
          angle: angle,
      })
    })
    // let lastElement = dots.pop()
    // if (lastElement !== undefined) {
    //   dots.unshift(lastElement);
    // }

    // opt 1: 标准雷达图
    const dotElementList: JSX.Element[] = dots.map((dot: any, i: number) => {
      return (
        <g key={`RCI-glyph-attr-dot-${dot.attrName}-${dot.value}-${i}`}>
          <circle key={`RCI-glyph-attr-dot-${dot.attrName}-${dot.value}-${i}`} id={`RCI-glyph-attr-dot-${dot.attrName}-${dot.value}-${i}`}
            cx={dot.x} cy={dot.y} r={2} fill='#98A7B2' // fill='#688fb0' fill='white'
            fillOpacity={0.9}
            onMouseEnter={(e) => {
              setTextLabelVisibility('visible')
            }}
            onMouseLeave={(e) => {
              setTextLabelVisibility('hidden')
            }}
          />
          <text id='impact-type-label'
                x={dot.x} y={dot.y} dx={4} dy={0} textAnchor="start" // transform='rotate(-10)'
                fontSize={4} fill="black" visibility={textLabelVisibility}>
            { (impactType === 'directImpact') ? attrNameList[i].slice(8, ) : attrNameList[i].slice(15, )}
          </text>
        </g>
      )
    })

    // opt 2: width有不同宽度的ripple, 用position和位置double encode: value - baseline (1)
    const rippleWidthRange = {
      min: 1,
      max: 8, // 6 pixels
    }
    // (1) calc max, min of diff_value to baseline
    const diffValueToBaselineList = dots.map(d => Math.abs(d.diff_value_to_baseline))
    const diffValueRange = d3.extent(diffValueToBaselineList) as [number, number]
    const rippleWidthScale = d3.scaleLinear()
      .domain(diffValueRange)
      .range([rippleWidthRange.min, rippleWidthRange.max])
    // console.log('diffValueToBaselineList: ', diffValueToBaselineList)
    // console.log('diffValueRange: ', diffValueRange)
    const dotElementList_ForVariedWidth: JSX.Element[] = []

    const dotList_OuterRing: any[] = []
    const dotList_InnerRing: any[] = []
    const dotList_ForRipple: any[] = []
    dots.forEach((dot, i: number) => {
      const angle = dot.angle
      const value = dot.value
      const diffValue = Math.abs(dot.diff_value_to_baseline)
      const dotWidth = rippleWidthScale(diffValue)
      const halfWidth = dotWidth / 2
      const cx = dot.x + halfWidth * Math.cos(angle - Math.PI/2)
      const cy = dot.y + halfWidth * Math.sin(angle - Math.PI/2)
      const r = Math.sqrt(Math.pow(cx, 2) + Math.pow(cy, 2))

      dotList_OuterRing.push({
        r: r,
        value: value + diffValue / 2,
        angle: angle,
        x: cx,
        y: cy
      })
      dotList_ForRipple.push({
        r: r,
        value: value + diffValue / 2,
        angle: angle,
        x: cx,
        y: cy
      })
      dotElementList_ForVariedWidth.push(
        <circle key={i} id={`RCI-ripple-dot-${dot.attrName}-${dot.value}-${i}`}
          cx={cx} cy={cy}
          // cx={dot.x} cy={dot.y}
          r={2} fill='none' // fill='#688fb0' fill='white'
          fillOpacity={0.9}
          onMouseEnter={(e) => {
            setTextLabelVisibility('visible')
          }}
          onMouseLeave={(e) => {
            setTextLabelVisibility('hidden')
          }}
        />
      )
    })
    _.forEachRight(dots, (dot, i: number) => {
      const value = dot.value
      const angle = dot.angle
      const diffValue = Math.abs(dot.diff_value_to_baseline)
      const dotWidth = rippleWidthScale(diffValue)
      const halfWidth = dotWidth / 2
      const cx = dot.x - halfWidth * Math.cos(angle - Math.PI/2)
      const cy = dot.y - halfWidth * Math.sin(angle - Math.PI/2)
      const r = Math.sqrt(Math.pow(cx, 2) + Math.pow(cy, 2))

      dotList_InnerRing.push({
        r: r,
        value: value + diffValue / 2,
        angle: angle,
        x: cx,
        y: cy
      })
      dotList_ForRipple.push({
        r: r,
        value: value + diffValue / 2,
        angle: angle,
        x: cx,
        y: cy
      })
      dotElementList_ForVariedWidth.push(
        <circle key={`RCI-ripple-dot-${dot.attrName}-${dot.value}-${i}`} id={`RCI-ripple-dot-${dot.attrName}-${dot.value}-${i}`}
          cx={cx} cy={cy}
          // cx={dot.x} cy={dot.y}
          r={2} fill='none' // fill='#688fb0' fill='white'
          fillOpacity={0.9}
          onMouseEnter={(e) => {
            setTextLabelVisibility('visible')
          }}
          onMouseLeave={(e) => {
            setTextLabelVisibility('hidden')
          }}
        />
      )
    })

    // draw line
    const lineGenerator2 = d3.lineRadial()
      .angle((d: any) => d.angle)
      .radius((d: any) => d.r)
      .curve(d3.curveCardinalClosed.tension(-0.2))
    // opt 1: use mask and two paths
    // input
    const lineCurvePath_OuterRing = lineGenerator2(dotList_OuterRing) as string
    const lineCurvePath_InnerRing = lineGenerator2(dotList_InnerRing) as string

    // console.log('grantGroupName: ', grantGroupName, typeof grantGroupName)

    const maskID = grantGroupName.replace(/\s/g, '_')
    const lineRippleElement = <g id={`lineRipple-${impactType}-${grantGroupName}`} key={`lineRipple-${impactType}-${grantGroupName}`}
    // const maskID = grant_field_name.replace(/\s/g, '_')
    // const lineRippleElement = <g id={`lineRipple-${impactType}-${grant_field_name}`} key={`lineRipple-${impactType}-${grant_field_name}`}
                                onMouseEnter={(e) => {
                                  if (impactType === 'directImpact') {
                                    setIsVisibleDirectImpactAxis(true)
                                  } else if (impactType === 'indirectImpact') {
                                    setIsVisibleIndirectImpactAxis(true)
                                  } else {
                                    console.log('error: unknown impactType!')
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  setIsVisibleDirectImpactAxis(false)
                                  setIsVisibleIndirectImpactAxis(false)
                                }}
                              >
                                <defs>
                                  <mask id={`${impactType}-${maskID}`}>
                                    <path
                                      d={lineCurvePath_OuterRing}
                                      fill={'white'}
                                    />
                                    <path
                                      d={lineCurvePath_InnerRing}
                                      fill={'black'}
                                    />
                                  </mask>
                                </defs>
                                <path
                                  mask={`url(#${impactType}-${maskID})`}// "url(#ringMask)"
                                  d={lineCurvePath_OuterRing}
                                  // fill={'url(#RCI-glyph-test'}
                                  fill={`url(#RCI-glyph-${impactType})`}
                                  // fillOpacity={0.8}
                                />
                                <path
                                  d={lineCurvePath_InnerRing}
                                  fill={`url(#RCI-glyph-${impactType}-innerCircle`} // RCI-glyph-indirectImpact-innerCircle
                                  // fillOpacity={0.15}
                                />
                              </g>

    // // opt 2: use one path (有问题，接不上)
    // const dots2 = _.cloneDeep(dots)
    // dots2.push({
    //   "x": dots[0].x,
    //   "y": dots[0].y,
    //   "attrName": dots[0].attrName,
    //   "value": dots[0].value,
    //   "diff_value_to_baseline": dots[0].diff_value_to_baseline,
    //   "angle": dots[0].angle - 0 // temp: hard code: to aviod overlap
    // })
    // // input
    // console.log('dotList_ForRipple: ', dotList_ForRipple)
    // // @ts-ignore
    // const lineCurvePath2 = lineGenerator2(dotList_ForRipple) as string
    // // refine path
    // let linePath2 = ''
    // const lineSegmentList = lineCurvePath2.split(/(?=[MC])/)
    // const numDot = dots.length
    // console.log('lineSegmentList: ', lineSegmentList)
    // console.log('numDot: ', numDot)
    // lineSegmentList.forEach((segment, i) => {
    //   if ((i !== numDot) && (i !== numDot * 2 + 1)) {
    //     linePath2 += segment
    //   } else {
    //     console.log('segment: ', segment)
    //     const [x1, y1, x2, y2, x3, y3] = segment.split(/[\s,]+/)
    //     console.log('[_, x1, y1, x2, y2, x3, y3]: ', [_, x1, y1, x2, y2, x3, y3])
    //     console.log('x3: ', x3)
    //     console.log('y3: ', y3)
    //     const x1_ = parseFloat(x1)
    //     const y1_ = parseFloat(y1)
    //     const x2_ = parseFloat(x2)
    //     const y2_ = parseFloat(y2)
    //     const x3_ = parseFloat(x3)
    //     const y3_ = parseFloat(y3)
    //     linePath2 += `L${x3}, ${y3}`

    //     // const x2_new = (x2_ + x3_) / 3
    //     // const y2_new = (y2_ + y3_) / 3
    //     // const x1_new = (x2_ + x3_) / 3
    //     // const y1_new = (y2_ + y3_) / 3
    //     // linePath2 += `C${x1_new},${y1_new},${x2_new},${y2_new},${x3_},${y3_}`
    //   }
    // })
    // const lineRipple = <path
    //                       // id={`ripple-path-${lineCurvePath2}`}
    //                       // d={lineCurvePath2}
    //                       d={linePath2}
    //                       fill={'red'}
    //                       fillOpacity={0.5}
    //                       // stroke="#3D4856" strokeWidth={2} strokeOpacity={0.5}
    //                     />


    // // 4. draw links between the dots
    // // @ts-ignore
    // const lineCurvePath = lineGenerator(dots) as string
    // const fill: string = (impactType === 'directImpact') ? '#273442' : '#B1B5B3'
    // const lineCurveElement = <path
    //                             // id={lineCurvePath}
    //                             d={lineCurvePath}
    //                             fill={fill}
    //                             // fill="#B1B5B3"
    //                             fillOpacity={0.5}
    //                             stroke="#3D4856" strokeWidth={2} strokeOpacity={0.5}
    //                           />
    // const linkElementList: JSX.Element[] = dots.map((dot: any, i: number) => {
    //   if (i < dots.length) {
    //     let dot1 = dots[i];
    //     let dot2 = dots[(i + 1) % dots.length];
    //     return (
    //       <line key={i} x1={dot1.x} y1={dot1.y} x2={dot2.x} y2={dot2.y} stroke="black" opacity={0.3}  />
    //     )
    //   }
    // }).filter((element): element is JSX.Element => element !== undefined); // Filter out undefined values

    return [
      // dotElementList,
      // linkElementList,
      // lineCurveElement,
      dotElementList_ForVariedWidth,
      lineRippleElement, // lineRipple,
      axisElementList,
    ]
  }

  // (1) direct impact
  const attrNameList_directImpact = attributeNameDict.directImpact
  const scaleDict_directImpact = attributeScale.directImpact
  const [
    // dotElementList_directImpact, linkElementList_directImpact, lineCurveElement_directImpact,
    dotElementList_ForVariedWidth_directImpact, lineRipple_directImpact, axisElementList_directImpact
  ] = drawImpactDotCurve(attrNameList_directImpact, scaleDict_directImpact, glyphDataHistorical, 'directImpact')
  // baseline circles
  const baselineCircle_directImpact = <circle cx={0} cy={0} r={scaleDict_directImpact(1)} fill="none" stroke="#CDCDCD" strokeDasharray={5} />

  // (2) indirect impact
  const attrNameList_indirectImpact = attributeNameDict.indirectImpact
  const scaleDict_indirectImpact = attributeScale.indirectImpact
  const [
    // dotElementList_indirectImpact, linkElementList_indirectImpact, lineCurveElement_indirectImpact,
    dotElementList_ForVariedWidth_indirectImpact, lineRipple_indirectImpact, axisElementList_indirectImpact
  ] = drawImpactDotCurve(attrNameList_indirectImpact, scaleDict_indirectImpact, glyphDataHistorical, 'indirectImpact')
  const baselineCircle_indirectImpact = <circle cx={0} cy={0} r={scaleDict_indirectImpact(1)} fill="none" stroke="#CDCDCD" strokeDasharray={5} />

  // inner circle: num_grant: historical + predicted
  const predictionGlyph: JSX.Element = <PredictionGlyph
                                        key={`prediction-g-${id}-${grantGroupName}-${glyphDataHistorical.num_grant}`}
                                        id={id}
                                        grantGroupName={grantGroupName}
                                        // grant_field_name={grant_field_name}
                                        glyphDataHistorical={glyphDataHistorical}
                                        glyphDataPredicted={glyphDataPredicted}
                                        glyphStyle = {glyphStyle}
                                        centerGrantNode_MaxMin = {centerGrantNode_MaxMin}
                                      />


  if (highlightedImpactFieldNode.impactFieldID === '') {
    return (
      <g key={`RCI-glyph-g-${id}-${grantGroupName}`}>
        {/* {lineCurveElement_directImpact}
        {lineCurveElement_indirectImpact} */}

        {/* {dotElementList_indirectImpact} */}
        {/* {linkElementList_indirectImpact} */}
        {dotElementList_ForVariedWidth_indirectImpact}
        {lineRipple_indirectImpact}
        {axisElementList_indirectImpact}

        {/* {dotElementList_directImpact} */}
        {/* {linkElementList_directImpact} */}
        {dotElementList_ForVariedWidth_directImpact}
        {lineRipple_directImpact}
        {axisElementList_directImpact}

        {baselineCircle_directImpact}
        {baselineCircle_indirectImpact}

        {predictionGlyph}

        {/* <text x={0} y={0} dx={+60} dy={-20} textAnchor="middle" fontSize={8} fill="black" transform='rotate(-30)'>{grant_field_name}</text> */}
        {/* <text x={0} y={0} dx={+0} dy={+64} textAnchor="middle" fontSize={13} fill="#404040" transform='rotate(0)'>{grant_field_name}</text> */}
      </g>
    )
  } else {
    return (
      <g key={`RCI-glyph-g-${id}-${grantGroupName}`}>
        {predictionGlyph}
      </g>
    )
  }


}

export default RCIGlyph;